import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function RequisitionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [decisionComment, setDecisionComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [ctcEdit, setCtcEdit] = useState(false);
  const [ctcMin, setCtcMin] = useState("");
  const [ctcMax, setCtcMax] = useState("");
  const publicBase = window.location.origin;

  const reload = async () => {
    const r = await api.get(`/requisitions/${id}`);
    setReq(r.data);
    const c = await api.get(`/candidates`, { params: { requisition_id: id } });
    setCandidates(c.data);
  };

  useEffect(() => {
    reload();
  }, [id]);

  if (!req) return <div className="p-8 text-quiet">Loading…</div>;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.patch(`/requisitions/${id}/decision`, { decision, comment: decisionComment });
      toast.success(`Requisition ${decision}ed`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRequisition = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.delete(`/requisitions/${id}`, { params: force ? { force: true } : {} });
      toast.success(
        r.data.candidates_removed
          ? `Requisition + ${r.data.candidates_removed} candidate(s) deleted`
          : "Requisition deleted",
      );
      nav("/requisitions");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Delete failed";
      if (status === 409 && !force) {
        // Candidates attached - ask for force confirmation
        if (window.confirm(`${detail}\n\nClick OK to force-delete the requisition AND all attached candidates. This cannot be undone.`)) {
          return deleteRequisition(true);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const isOwner = (req.raised_by || {}).id === user?.id;
  const canDelete = user?.role === "hr";
  const canClose = user?.role === "hr" && req.status === "open";

  const closeRequisition = async () => {
    if (!window.confirm("Close this requisition? It will no longer accept new applicants.")) return;
    setBusy(true);
    try {
      await api.patch(`/requisitions/${id}/close`);
      toast.success("Requisition closed");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to close");
    } finally {
      setBusy(false);
    }
  };

  const startCtcEdit = () => {
    setCtcMin(req.ctc_min ?? "");
    setCtcMax(req.ctc_max ?? "");
    setCtcEdit(true);
  };

  const saveCtc = async () => {
    if (ctcMin !== "" && ctcMax !== "" && Number(ctcMin) > Number(ctcMax)) {
      return toast.error("Minimum CTC cannot exceed maximum CTC");
    }
    setBusy(true);
    try {
      await api.patch(`/requisitions/${id}/ctc`, {
        ctc_min: ctcMin === "" ? null : Number(ctcMin),
        ctc_max: ctcMax === "" ? null : Number(ctcMax),
      });
      toast.success("CTC updated");
      setCtcEdit(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update CTC");
    } finally {
      setBusy(false);
    }
  };

  const applyLink = req.public_apply_id ? `${publicBase}/apply/${req.public_apply_id}` : null;

  const downloadJd = async () => {
    try {
      const r = await api.get(`/requisitions/${id}/jd`);
      const { filename, mime, b64 } = r.data;
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download");
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="requisition-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/requisitions" className="text-xs text-quiet hover:text-ink font-mono">← Requisitions</Link>
        <div className="flex items-center gap-2">
          {canClose && (
            <button
              data-testid="close-req-btn"
              onClick={closeRequisition}
              disabled={busy}
              className="text-xs font-mono uppercase tracking-wider text-ink hover:bg-paper border border-ink px-3 py-1.5 disabled:opacity-50"
            >
              Close requisition
            </button>
          )}
          {canDelete && (
            <button
              data-testid="delete-req-btn"
              onClick={() => setConfirmDelete(true)}
              className="text-xs font-mono uppercase tracking-wider text-[#EF4444] hover:bg-[#FEF2F2] border border-[#EF4444] px-3 py-1.5"
            >
              Delete requisition
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            {req.requisition_code || "DRAFT"} · {req.department} · {req.location}
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">{req.title}</h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatusBadge status={req.status} testId="req-status-badge" />
            <span className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-quiet bg-white">
              Urgency · {req.urgency}
            </span>
            <span className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-quiet bg-white">
              HC · {req.headcount}
            </span>
            {req.raised_by?.name && (
              <span
                data-testid="raised-by-header-pill"
                className="px-2 py-1 border border-rule text-[10px] font-mono uppercase tracking-wider text-quiet bg-white"
              >
                Raised by · {req.raised_by.name}
                {req.raised_by.role && (
                  <span className="text-ink"> ({req.raised_by.role})</span>
                )}
              </span>
            )}
            {req.submitted_by_external?.name && (
              <span
                data-testid="requested-by-header-pill"
                className="px-2 py-1 border border-klein text-[10px] font-mono uppercase tracking-wider text-klein bg-white"
              >
                Requested by · {req.submitted_by_external.name}
                {req.submitted_by_external.team && (
                  <span className="text-ink"> ({req.submitted_by_external.team})</span>
                )}
              </span>
            )}
          </div>
        </div>

        {req.status === "open" && applyLink && (
          <div className="bg-white border border-rule p-4 max-w-sm">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Public apply link</div>
            <div className="mt-2 text-xs font-mono break-all text-ink">{applyLink}</div>
            <button
              data-testid="copy-apply-link"
              onClick={() => {
                navigator.clipboard.writeText(applyLink);
                toast.success("Link copied");
              }}
              className="mt-3 w-full border border-ink hover:bg-paper text-ink px-3 py-2 text-xs font-medium"
            >
              Copy link
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <section className="lg:col-span-2 bg-white border border-rule">
          <div className="px-5 py-4 border-b border-rule font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
            Job description
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Skills</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(req.skills || "").split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                  <span key={s} className="px-2 py-1 bg-paper border border-rule text-xs font-mono">{s}</span>
                ))}
              </div>
            </div>
            <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{req.description}</div>
            {req.jd_filename && (
              <div className="pt-4 border-t border-rule flex items-center justify-between gap-3 bg-paper -mx-5 px-5 py-3 mt-4">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Attached JD file</div>
                  <div className="text-sm text-ink truncate" data-testid="jd-attached-name">{req.jd_filename}</div>
                </div>
                <button
                  data-testid="download-jd-btn"
                  onClick={downloadJd}
                  className="bg-ink hover:bg-black text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                >
                  Download ↓
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-rule">
              <Detail label="Experience" value={`${req.experience_min}–${req.experience_max} yrs`} />
              <div>
                <div className="flex items-center gap-3">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">CTC</div>
                  {req.status === "pending_review" && user?.role === "hr" && !ctcEdit && (
                    <button
                      data-testid="edit-ctc-btn"
                      onClick={startCtcEdit}
                      className="font-mono text-[10px] uppercase tracking-wider text-klein hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {ctcEdit ? (
                  <div className="mt-2 space-y-2" data-testid="ctc-edit-panel">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        data-testid="edit-ctc-min"
                        type="number"
                        min={0}
                        value={ctcMin}
                        onChange={(e) => setCtcMin(e.target.value)}
                        placeholder="Min LPA"
                        className="border border-rule px-2 py-1.5 text-sm focus:outline-none focus:border-klein"
                      />
                      <input
                        data-testid="edit-ctc-max"
                        type="number"
                        min={ctcMin || 0}
                        value={ctcMax}
                        onChange={(e) => setCtcMax(e.target.value)}
                        placeholder="Max LPA"
                        className="border border-rule px-2 py-1.5 text-sm focus:outline-none focus:border-klein"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="save-ctc-btn"
                        disabled={busy}
                        onClick={saveCtc}
                        className="bg-ink hover:bg-black text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        data-testid="cancel-ctc-btn"
                        onClick={() => setCtcEdit(false)}
                        className="border border-rule text-quiet hover:text-ink px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-ink mt-1" data-testid="ctc-display">
                    {req.ctc_min || req.ctc_max ? `${req.ctc_min ?? "—"} – ${req.ctc_max ?? "—"} LPA` : "—"}
                  </div>
                )}
              </div>
            </div>
            {req.raised_by && (
              <div
                className="border-t border-rule pt-4 -mx-5 px-5 py-4 bg-paper mt-4"
                data-testid="raised-by-card"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
                  Raised by
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Name</span>
                    <span className="text-ink" data-testid="raised-by-name">{req.raised_by.name || "—"}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Email</span>
                    <a
                      href={req.raised_by.email ? `mailto:${req.raised_by.email}` : undefined}
                      className="text-ink hover:text-klein break-all"
                      data-testid="raised-by-email"
                    >
                      {req.raised_by.email || "—"}
                    </a>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Role</span>
                    <span className="text-ink capitalize" data-testid="raised-by-role">{req.raised_by.role || "—"}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Raised on</span>
                    <span className="text-ink" data-testid="raised-by-date">
                      {req.created_at ? new Date(req.created_at).toLocaleString() : "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {req.submitted_by_external && (
              <div
                className="border-t border-rule pt-4 -mx-5 px-5 py-4 bg-white mt-4 border-b border-l-2 border-l-klein"
                data-testid="requested-by-card"
              >
                <div className="flex items-center gap-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-klein">
                    Requested by (Hiring Manager)
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-quiet">
                    · filled the raise-requisition form
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Name</span>
                    <span className="text-ink" data-testid="requested-by-name">{req.submitted_by_external.name || "—"}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Email</span>
                    <a
                      href={req.submitted_by_external.email ? `mailto:${req.submitted_by_external.email}` : undefined}
                      className="text-ink hover:text-klein break-all"
                      data-testid="requested-by-email"
                    >
                      {req.submitted_by_external.email || "—"}
                    </a>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2">Team</span>
                    <span className="text-ink" data-testid="requested-by-team">{req.submitted_by_external.team || "—"}</span>
                  </div>
                  {req.submitted_by_external.note && (
                    <div className="sm:col-span-2">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-quiet mr-2 block mb-1">Note</span>
                      <span className="text-ink whitespace-pre-wrap" data-testid="requested-by-note">{req.submitted_by_external.note}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {req.hr_comment && (
              <div className="border-t border-rule pt-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">HR Comment</div>
                <div className="text-sm text-ink mt-1">{req.hr_comment}</div>
              </div>
            )}
          </div>
        </section>

        <aside className="space-y-4">
          {req.status === "pending_review" && user?.role === "hr" && (
            <div className="bg-white border border-rule p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">HR Review</div>
              <p className="text-sm text-quiet mt-2">SLA: 3 working days from creation.</p>
              <textarea
                data-testid="hr-decision-comment"
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                placeholder="Comment / reason"
                rows={3}
                className="w-full mt-3 border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein"
              />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  data-testid="reject-req-btn"
                  disabled={busy}
                  onClick={() => decide("reject")}
                  className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-2 text-sm"
                >
                  Reject
                </button>
                <button
                  data-testid="accept-req-btn"
                  disabled={busy}
                  onClick={() => decide("accept")}
                  className="bg-klein hover:bg-klein-hover text-white px-3 py-2 text-sm"
                >
                  Accept →
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-rule">
            <div className="px-5 py-4 border-b border-rule flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
                Candidates ({candidates.length})
              </div>
              <Link
                to={`/candidates?requisition_id=${req.id}`}
                className="text-xs text-klein hover:underline"
                data-testid="link-view-candidates"
              >
                View all →
              </Link>
            </div>
            <div className="divide-y divide-rule max-h-[400px] overflow-auto">
              {candidates.slice(0, 10).map((c) => (
                <Link
                  key={c.id}
                  to={`/candidates/${c.id}`}
                  className="block px-5 py-3 hover:bg-paper"
                  data-testid={`req-candidate-${c.id}`}
                >
                  <div className="text-ink text-sm">{c.full_name}</div>
                  <div className="text-xs text-quiet font-mono mt-1 flex items-center gap-2">
                    <StatusBadge status={c.status} />
                    {c.ai_score?.overall != null && <span>· {c.ai_score.overall}</span>}
                  </div>
                </Link>
              ))}
              {candidates.length === 0 && (
                <div className="px-5 py-8 text-center text-quiet text-xs">No candidates yet.</div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-ink max-w-md w-full p-6 fade-in" data-testid="delete-confirm-modal">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EF4444]">Destructive action</div>
            <h3 className="font-serif text-3xl text-ink mt-1 leading-tight">Delete this requisition?</h3>
            <p className="text-sm text-quiet mt-4">
              <span className="text-ink">{req.title}</span>
              {req.requisition_code && <> · <span className="font-mono">{req.requisition_code}</span></>}
              <br />
              This cannot be undone.
              {candidates.length > 0 && (
                <>
                  {" "}
                  <span className="text-[#EF4444]">
                    {candidates.length} candidate{candidates.length > 1 ? "s" : ""} attached to this requisition will also be deleted.
                  </span>
                </>
              )}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="border border-ink px-4 py-2 text-sm hover:bg-paper"
                data-testid="cancel-delete"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete"
                onClick={() => deleteRequisition(candidates.length > 0)}
                disabled={busy}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {busy ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">{label}</div>
    <div className="text-sm text-ink mt-1">{value}</div>
  </div>
);
